import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from './DashboardLayout';
import { listCashSessions, getBranches, getCashSession } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Loader2, Lock, Unlock, Calendar, Eye, FileText } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

export default function AdminCashSessionsPage() {
  const [sessions, setSessions] = useState([]);
  const [branches, setBranches] = useState([]);
  const [filterBranch, setFilterBranch] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [branchesRes] = await Promise.all([getBranches()]);
        setBranches(branchesRes.data);
      } catch (e) { /* silent */ }
    })();
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (filterBranch !== 'all') params.branch_id = filterBranch;
    if (filterStatus !== 'all') params.status = filterStatus;
    listCashSessions(params)
      .then((res) => setSessions(res.data))
      .catch(() => { /* silent */ })
      .finally(() => setLoading(false));
  }, [filterBranch, filterStatus]);

  const handleViewDetail = async (session) => {
    try {
      const { data } = await getCashSession(session.id);
      setDetail(data);
    } catch (e) {
      setDetail(session);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fadeIn">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Historial de Caja</h1>
          <p className="text-slate-600 mt-1">Aperturas y cierres de caja por sucursal.</p>
        </div>

        <div className="flex flex-wrap gap-3">
          <Select value={filterBranch} onValueChange={setFilterBranch}>
            <SelectTrigger className="w-56" data-testid="filter-branch-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las sucursales</SelectItem>
              {branches.map((b) => (
                <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-44" data-testid="filter-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="abierta">Abierta</SelectItem>
              <SelectItem value="cerrada">Cerrada</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="card-base">
          {loading ? (
            <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : sessions.length === 0 ? (
            <div className="p-10 text-center text-slate-500">Sin sesiones registradas</div>
          ) : (
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="table-header">Fecha</th>
                  <th className="table-header">Sucursal</th>
                  <th className="table-header">Apertura</th>
                  <th className="table-header">Cierre</th>
                  <th className="table-header">Esperado</th>
                  <th className="table-header">Diferencia</th>
                  <th className="table-header">Estado</th>
                  <th className="table-header text-right">Detalle</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => {
                  const diff = s.difference;
                  const diffColor = diff === null || diff === undefined
                    ? 'text-slate-400'
                    : Math.abs(diff) < 0.01
                      ? 'text-slate-600'
                      : diff > 0
                        ? 'text-emerald-600'
                        : 'text-red-600';
                  return (
                    <tr key={s.id} className="table-row" data-testid={`session-row-${s.id}`}>
                      <td className="table-cell">
                        <div className="flex items-center gap-2 text-slate-700">
                          <Calendar className="w-3.5 h-3.5 text-slate-400" />
                          {s.date}
                        </div>
                        <div className="text-xs text-slate-400 mt-0.5">
                          {s.opened_by_name}
                        </div>
                      </td>
                      <td className="table-cell text-slate-700">{s.branch_name}</td>
                      <td className="table-cell font-mono">{formatCurrency(s.opening_amount || 0)}</td>
                      <td className="table-cell font-mono">
                        {s.closing_amount !== null && s.closing_amount !== undefined
                          ? formatCurrency(s.closing_amount)
                          : '—'}
                      </td>
                      <td className="table-cell font-mono text-slate-600">
                        {s.expected_amount !== null && s.expected_amount !== undefined
                          ? formatCurrency(s.expected_amount)
                          : '—'}
                      </td>
                      <td className={`table-cell font-mono font-semibold ${diffColor}`}>
                        {diff !== null && diff !== undefined
                          ? (diff >= 0 ? '+' : '') + formatCurrency(diff)
                          : '—'}
                      </td>
                      <td className="table-cell">
                        <span className={`status-badge inline-flex items-center gap-1 ${
                          s.status === 'abierta'
                            ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                            : 'bg-slate-100 text-slate-700 border-slate-200'
                        }`}>
                          {s.status === 'abierta' ? <Unlock className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                          {s.status}
                        </span>
                      </td>
                      <td className="table-cell text-right">
                        <div className="flex items-center gap-2 justify-end">
                          <Link
                            to={`/admin/caja/${s.id}/reporte`}
                            className="inline-flex items-center gap-1 text-sm text-emerald-600 hover:text-emerald-700"
                            data-testid={`view-report-${s.id}`}
                          >
                            <FileText className="w-4 h-4" />
                            Corte
                          </Link>
                          <button
                            type="button"
                            onClick={() => handleViewDetail(s)}
                            className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
                            data-testid={`view-session-${s.id}`}
                          >
                            <Eye className="w-4 h-4" />
                            Ver
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Sesión de caja</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-xs text-slate-500">Sucursal</div>
                  <div className="font-medium">{detail.branch_name}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Fecha</div>
                  <div className="font-medium">{detail.date}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Abrió</div>
                  <div className="font-medium">{detail.opened_by_name}</div>
                  <div className="text-xs text-slate-400">{detail.opened_at ? new Date(detail.opened_at).toLocaleString('es-MX') : '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Cerró</div>
                  <div className="font-medium">{detail.closed_by_name || '—'}</div>
                  <div className="text-xs text-slate-400">{detail.closed_at ? new Date(detail.closed_at).toLocaleString('es-MX') : '—'}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 p-3 bg-slate-50 rounded-xl">
                <div>
                  <div className="text-xs text-slate-500">Apertura</div>
                  <div className="font-mono font-semibold">{formatCurrency(detail.opening_amount || 0)}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Ingresos</div>
                  <div className="font-mono font-semibold text-emerald-600">+{formatCurrency(detail.ingresos_total || 0)}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Egresos</div>
                  <div className="font-mono font-semibold text-red-600">-{formatCurrency(detail.egresos_total || 0)}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Esperado</div>
                  <div className="font-mono font-bold text-blue-700">{formatCurrency(detail.expected_amount ?? detail.current_expected ?? 0)}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Contado</div>
                  <div className="font-mono font-semibold">{detail.closing_amount !== null && detail.closing_amount !== undefined ? formatCurrency(detail.closing_amount) : '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Diferencia</div>
                  <div className={`font-mono font-bold ${
                    detail.difference === null || detail.difference === undefined
                      ? 'text-slate-400'
                      : Math.abs(detail.difference) < 0.01
                        ? 'text-slate-600'
                        : detail.difference > 0
                          ? 'text-emerald-600'
                          : 'text-red-600'
                  }`}>
                    {detail.difference !== null && detail.difference !== undefined
                      ? (detail.difference >= 0 ? '+' : '') + formatCurrency(detail.difference)
                      : '—'}
                  </div>
                </div>
              </div>

              {detail.opening_notes && (
                <div>
                  <div className="text-xs text-slate-500">Notas de apertura</div>
                  <div className="text-slate-700">{detail.opening_notes}</div>
                </div>
              )}
              {detail.closing_notes && (
                <div>
                  <div className="text-xs text-slate-500">Notas de cierre</div>
                  <div className="text-slate-700">{detail.closing_notes}</div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
